import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserPreferencesStore } from '@/store/storeUserPreferences';
import {
  formatDateCore,
  formatDateTimeCore,
  formatNumberCore,
  resolveUserPrefFormatSettings,
  type FormatNumberOptions,
  type UserPrefFormatSettings
} from '@/composables/userPrefFormattingCore';

export type { FormatNumberOptions } from '@/composables/userPrefFormattingCore';

/**
 * Composable som eksponerer user-prefs-relaterte formaterings-verdier i en form som
 * passer å sende inn som props til rene presentasjons-komponenter (BaseComponents skal
 * ikke selv lese stores). Henter verdier fra `useUserPreferencesStore` og gir defaults
 * der user-prefs ikke er lastet enda.
 *
 * Brukes typisk slik i en host-komponent:
 *
 *   const { dateFormat, numberThousandsSeparator, numberDecimalSeparator } = useUserPrefFormatting();
 *   // <BaseDateInput :date-format="dateFormat" ... />
 *
 * I tillegg (WI 2086) ferdige formatterings-funksjoner for visningskomponenter som
 * selv kan lese stores: `formatNumber`, `formatDate`, `formatDateTime`.
 * Selve formaterings-adferden ligger i `userPrefFormattingCore`.
 */
export function useUserPrefFormatting () {
  const userPreferencesStore = useUserPreferencesStore();
  const { userPreferences } = storeToRefs(userPreferencesStore);

  const settings = computed<UserPrefFormatSettings>(() => resolveUserPrefFormatSettings(userPreferences.value));

  /** 'DMY' | 'MDY' | 'YMD'. Fallback 'DMY' (norsk default). */
  const dateFormat = computed<string>(() => settings.value.dateFormat);

  /** Tusenskille-separator for tall (default mellomrom). */
  const numberThousandsSeparator = computed<string>(() => settings.value.thousandsSeparator);

  /** Desimal-separator for tall (default komma). */
  const numberDecimalSeparator = computed<string>(() => settings.value.decimalSeparator);

  /** IANA-tidssone for dato-/tidsvisning (default 'Europe/Oslo', jf. RegionalSettings). */
  const timeZone = computed<string>(() => settings.value.timeZone);

  /**
   * Formaterer et tall med brukerens tusen-/desimalskille. Ikke-endelige verdier
   * (NaN/Infinity) returneres verbatim, som `toLocaleString` ville gjort.
   */
  function formatNumber (value: number, options: FormatNumberOptions = {}): string {
    return formatNumberCore(value, settings.value, options);
  }

  /** Dato uten klokkeslett, etter brukerens datoformat og tidssone. */
  function formatDate (value: string | Date | null | undefined): string {
    return formatDateCore(value, settings.value);
  }

  /** Dato + klokkeslett (HH:mm), etter brukerens datoformat og tidssone. */
  function formatDateTime (value: string | Date | null | undefined): string {
    return formatDateTimeCore(value, settings.value);
  }

  return {
    dateFormat,
    numberThousandsSeparator,
    numberDecimalSeparator,
    timeZone,
    formatNumber,
    formatDate,
    formatDateTime
  };
}
